import type { FontFamily } from 'database';
import localFont from 'next/font/local';

export interface Font {
  font: FontFamily;
  className: string;
  variable?: string;
}

// Served from the repo, latin subset: a build no longer asks Google Fonts,
// which at times answers build machines with file URLs next/font cannot read.
// The files are the ones next/font/google fetched for the same declarations.

export const inter = localFont({
  src: './files/inter-100-900-normal.woff2',
  weight: '100 900',
  style: 'normal',
  display: 'swap',
});

export const inter_classname = inter.className;

export const roboto = localFont({
  src: './files/roboto-400-700-normal.woff2',
  weight: '400 700',
  style: 'normal',
  preload: false,
  display: 'swap',
});

export const roboto_classname = roboto.className;

export const roboto_mono = localFont({
  src: './files/roboto_mono-100-700-normal.woff2',
  weight: '100 700',
  style: 'normal',
  preload: false,
  display: 'swap',
});

export const space_grotesk = localFont({
  src: './files/space_grotesk-400-700-normal.woff2',
  weight: '400 700',
  style: 'normal',
  preload: false,
  display: 'swap',
});

export const space_mono = localFont({
  src: [
    {
      path: './files/space_mono-400-normal.woff2',
      weight: '400',
      style: 'normal',
    },
    {
      path: './files/space_mono-700-normal.woff2',
      weight: '700',
      style: 'normal',
    },
  ],
  preload: false,
  display: 'swap',
});

export const playfair_display = localFont({
  src: './files/playfair_display-400-900-normal.woff2',
  weight: '400 900',
  style: 'normal',
  preload: false,
  display: 'swap',
});

export const prata = localFont({
  src: './files/prata-400-normal.woff2',
  weight: '400',
  style: 'normal',
  preload: false,
  display: 'swap',
});

export const glegoo = localFont({
  src: [
    { path: './files/glegoo-400-normal.woff2', weight: '400', style: 'normal' },
    { path: './files/glegoo-700-normal.woff2', weight: '700', style: 'normal' },
  ],
  preload: false,
  display: 'swap',
});

export const roboto_condensed = localFont({
  src: './files/roboto_condensed-400-700-normal.woff2',
  weight: '400 700',
  style: 'normal',
  preload: false,
  display: 'swap',
});

export const roboto_slab = localFont({
  src: './files/roboto_slab-400-700-normal.woff2',
  weight: '400 700',
  style: 'normal',
  preload: false,
  display: 'swap',
});

export const ibm_plex_sans_condensed = localFont({
  src: [
    {
      path: './files/ibm_plex_sans_condensed-400-normal.woff2',
      weight: '400',
      style: 'normal',
    },
    {
      path: './files/ibm_plex_sans_condensed-700-normal.woff2',
      weight: '700',
      style: 'normal',
    },
  ],
  preload: false,
  display: 'swap',
  variable: '--font-theme',
});

export const ibm_plex_sans_condensed_classname =
  ibm_plex_sans_condensed.className;

export const ibm_plex_sans_condensed_var = ibm_plex_sans_condensed.variable;

export const ubuntu_condensed = localFont({
  src: './files/ubuntu_condensed-400-normal.woff2',
  weight: '400',
  style: 'normal',
  preload: false,
  display: 'swap',
});

export const barlow_condensed = localFont({
  src: [
    {
      path: './files/barlow_condensed-400-normal.woff2',
      weight: '400',
      style: 'normal',
    },
    {
      path: './files/barlow_condensed-700-normal.woff2',
      weight: '700',
      style: 'normal',
    },
  ],
  preload: false,
  display: 'swap',
});

export const saira_condensed = localFont({
  src: [
    {
      path: './files/saira_condensed-400-normal.woff2',
      weight: '400',
      style: 'normal',
    },
    {
      path: './files/saira_condensed-700-normal.woff2',
      weight: '700',
      style: 'normal',
    },
  ],
  preload: false,
  display: 'swap',
});

// Novedades' editorial serif: headlines, decks and body copy of the notes.
export const newsreader = localFont({
  src: './files/newsreader-200-800-normal.woff2',
  weight: '200 800',
  style: 'normal',
  preload: false,
  display: 'swap',
  variable: '--font-editorial',
});

const config = {
  variable: '--font-theme',
};

export const fonts: Font[] = [
  { ...config, font: 'inter', className: inter.className },
  {
    ...config,
    font: 'barlow_condensed',
    className: barlow_condensed.className,
  },
  { ...config, font: 'glegoo', className: glegoo.className },
  {
    ...config,
    font: 'ibm_plex_sans_condensed',
    className: ibm_plex_sans_condensed.className,
  },
  {
    ...config,
    font: 'playfair_display',
    className: playfair_display.className,
  },
  { ...config, font: 'prata', className: prata.className },
  { ...config, font: 'roboto', className: roboto.className },
  { ...config, font: 'robot_flex', className: roboto.className },
  {
    ...config,
    font: 'roboto_condensed',
    className: roboto_condensed.className,
  },
  { ...config, font: 'roboto_mono', className: roboto_mono.className },
  { ...config, font: 'roboto_slab', className: roboto_slab.className },
  { ...config, font: 'saira_condensed', className: saira_condensed.className },
  { ...config, font: 'space_grotesk', className: space_grotesk.className },
  { ...config, font: 'space_mono', className: space_mono.className },
  {
    ...config,
    font: 'ubuntu_condensed',
    className: ubuntu_condensed.className,
  },
];

export const fontClassName = (font: FontFamily): string => {
  const target = fonts.find((f) => f.font === font);

  return target?.className || '';
};
